import { DC } from "../../constants";
import { Quotes } from "../quotes";

import { DarkMatterDimensions } from "./dark-matter-dimension";

export const Laitela = {
  displayName: "Lai'tela",
  possessiveName: "Lai'tela's",
  get celestial() {
    return player.celestials.laitela;
  },
  get isUnlocked() {
    return ImaginaryUpgrade(15).isBought;
  },
  initializeRun() {
    clearCelestialRuns();
    this.celestial.run = true;
  },
  get isRunning() {
    return this.celestial.run;
  },
  get difficultyTier() {
    return player.celestials.laitela.difficultyTier;
  },
  set difficultyTier(tier) {
    player.celestials.laitela.difficultyTier = tier;
  },
  get maxAllowedDimension() {
    return 8 - this.difficultyTier;
  },
  get isFullyDestabilized() {
    return Laitela.maxAllowedDimension === 0;
  },
  get continuumUnlocked() {
    return (ImaginaryUpgrade(15).isBought || AtomUpgrade(4).isBought) && !Pelle.isDisabled("continuum");
  },
  get continuumActive() {
    return this.continuumUnlocked && !player.auto.continuumDisabled.AD && !Pelle.isDisabled("continuum");
  },
  setContinuum(x) {
    player.auto.continuumDisabled.AD = !x;
    // If continuum is now not disabled (i.e. is enabled) we update the relevant requirement check.
    if (!player.auto.continuumDisabled.AD) {
      player.requirementChecks.reality.noContinuum = false;
    }
  },
  get matterExtraPurchaseFactor() {
    const preAtom =
      1 +
      0.5 *
        Math.pow(Decimal.pLog10(Currency.darkMatter.max) / 50, 0.4) *
        (1 + SingularityMilestone.continuumMult.effectOrDefault(0));
    const postAtom = preAtom * AtomicParticle(0).effects[0] * Effects.product(AtomUpgrade(4));
    return postAtom;
  },
  get realityReward() {
    return Math.clampMin(
      Math.pow(100, this.difficultyTier) * Math.pow(360 / player.celestials.laitela.fastestCompletion, 2),
      1,
    );
  },
  // Note that entropy goes from 0 to 1, with 1 being completion
  get entropyGainPerSecond() {
    return Math.clamp(Math.pow(Currency.antimatter.value.add(1).log10() / 1e11, 2), 0, 100) / 200;
  },
  get darkMatterMultGain() {
    return Decimal.pow(Currency.darkMatter.value.dividedBy(this.annihilationDMRequirement).plus(1).log10(), 1.5).mul(
      ImaginaryUpgrade(21).effectOrDefault(1),
    );
  },
  get darkMatterMult() {
    return this.celestial.darkMatterMult;
  },
  get darkMatterMultRatio() {
    return this.darkMatterMultGain.div(this.celestial.darkMatterMult).add(1);
  },
  get annihilationUnlocked() {
    return ImaginaryUpgrade(19).isBought;
  },
  get annihilationDMRequirement() {
    return 1e60;
  },
  get canAnnihilate() {
    return Laitela.annihilationUnlocked && Currency.darkMatter.gte(this.annihilationDMRequirement);
  },
  annihilate(force) {
    if (!force && !this.canAnnihilate) return false;
    this.celestial.darkMatterMult = this.celestial.darkMatterMult.add(this.darkMatterMultGain);
    DarkMatterDimensions.reset();
    if (!force) {
      Laitela.quotes.annihilation.show();
      Achievement(176).unlock();
    }
    return true;
  },
  // Greedily buys the cheapest available upgrade until none are affordable
  maxAllDMDimensions(maxTier) {
    // Note that tier is 1-indexed
    const unlockedDimensions = DarkMatterDimensions.all.filter((d) => d.isUnlocked && d.tier <= maxTier);
    const upgradeInfo = unlockedDimensions
      .map((d) => [
        [
          d.rawIntervalCost,
          d.intervalCostIncrease,
          d.maxIntervalPurchases,
          d.data.intervalUpgrades,
          (x) => d.buyManyInterval(x),
        ],
        [d.rawPowerDMCost, d.powerDMCostIncrease, Infinity, d.data.powerDMUpgrades, (x) => d.buyManyPowerDM(x)],
        [d.rawPowerDECost, d.powerDECostIncrease, Infinity, d.data.powerDEUpgrade, (x) => d.buyManyPowerDE(x)],
      ])
      .flat(1);
    const buy = function (upgrade, purchases) {
      upgrade[4](purchases);
      upgrade[3] += purchases;
      upgrade[0] = upgrade[0].times(Decimal.pow(upgrade[1], purchases));
      upgrade[2] -= purchases;
    };
    // Buy everything costing less than 0.02 of initial matter.
    const darkMatter = Currency.darkMatter.value;
    for (const upgrade of upgradeInfo) {
      const purchases = Math.clamp(Math.floor(darkMatter.times(0.02).div(upgrade[0]).log(upgrade[1])), 0, upgrade[2]);
      buy(upgrade, purchases);
    }
    // Make sure that we don't forever buy 1 because precision issues
    while (upgradeInfo.some((upgrade) => upgrade[0].lte(darkMatter) && upgrade[2] > 0 && upgrade[3] + 1 > upgrade[3])) {
      const cheapestUpgrade = upgradeInfo
        .filter((upgrade) => upgrade[2] > 0)
        .sort((a, b) => a[0].minus(b[0]).sign())[0];
      buy(cheapestUpgrade, 1);
    }
  },
  reset() {
    this.annihilate(true);
    this.celestial.darkMatterMult = DC.D1;
    Currency.darkMatter.max = DC.D1;
    Currency.darkMatter.reset();
    Currency.singularities.reset();
    this.celestial.fastestCompletion = 3600;
    this.celestial.difficultyTier = 0;
    this.celestial.singularityCapIncreases = 0;
  },
  quotes: Quotes.laitela,
  symbol: "ᛝ",
};

EventHub.logic.on(GAME_EVENT.TAB_CHANGED, () => {
  if (Tab.celestials.laitela.isOpen) Laitela.quotes.unlock.show();
});
